import type { WidgetDef, WidgetLayoutEntry, WidgetSize } from '../components/WidgetGrid';
import { SIZE_COLS } from '../components/widgets/widgetVisibility';

const SIZES: WidgetSize[] = ['compact', 'standard', 'expanded'];

/** Size tier → free width: an entry that carries a legacy `size` but no
 *  `w` gets `w = SIZE_COLS[size]` (compact 3 / standard 6 / expanded 12).
 *  `size` is left in place so older clients can still read the layout.
 *  Returns the SAME entry when nothing changes. */
function withWidth(e: WidgetLayoutEntry): WidgetLayoutEntry {
  if (typeof e.w === 'number' || !e.size || !SIZES.includes(e.size)) return e;
  return { ...e, w: SIZE_COLS[e.size] };
}

/**
 * Migrate saved dashboard layouts (user_prefs blob + saved Views) from the
 * retired combined "Schedule & Milestones" widget to its two successors:
 * schedule_timeline → phases + deliverables (deliverables keeps the saved
 * size so an expanded schedule stays an expanded Gantt). Idempotent; runs
 * at render so stored configs migrate lazily on next save.
 *
 * `sectionOrder` (Project Canvas R1) threads a user's old Project-panel
 * section order into `ensureProjectDetailWidgets`, the last step — see
 * that function's doc.
 */
export function migrateDashboardLayout(
  entries: WidgetLayoutEntry[],
  sectionOrder?: PanelSectionId[]
): WidgetLayoutEntry[] {
  const widened = entries.map(withWidth);
  const changed = widened.some((e, i) => e !== entries[i]);
  const afterSchedule = migrateScheduleTimeline(changed ? widened : entries);
  const afterProjectMerge = migrateProjectWidgetMerge(afterSchedule);
  const afterAttention = ensureAttentionWidget(migrateReportWidgetIds(afterProjectMerge));
  return ensureProjectTokensWidget(ensureProjectDetailWidgets(afterAttention, sectionOrder));
}

/** Attention (T3) is new — a saved layout that predates it gets it once,
 *  full width at the top: `{id:'attention', w:12, x:0, y:0}` inserted at
 *  the front (existing entries keep their y; the grid's compaction pushes
 *  them down). An entry already present — even `hidden: true` — counts, so
 *  a user who hid it never gets it back. An empty layout means "use the
 *  default" and is left empty. Idempotent; same reference when unchanged.
 *  Viewers without the widget's key never see it (WidgetGrid filters by
 *  the permitted defs). */
export function ensureAttentionWidget(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  if (entries.length === 0 || entries.some((e) => e.id === 'attention')) return entries;
  return [{ id: 'attention', w: 12, x: 0, y: 0 }, ...entries];
}

/** T3B stock-set merge — retired report-widget ids rename onto their
 *  merged replacement (first position in the layout wins; a later
 *  duplicate of the SAME target is dropped, never doubled):
 *  budget_hours|budget_dollars|burn_rate|variance → budget,
 *  my_tasks|risks → tasks, ar_aging → invoices. team_utilization
 *  folds into an EXISTING `team` entry (dropped if one is already
 *  present; renamed in place otherwise so a layout is never left with
 *  two `team` rows). phase_health and recent_activity retired outright
 *  — `attention` is new and NOT inserted here (that is
 *  `ensureAttentionWidget`, run after this in migrateDashboardLayout).
 *  Widgets R2 (Decision 5, 10 → 8 stock widgets): `forma` folds into
 *  `specs` (a strip at the top) and `profitability` folds into `budget`
 *  (a section) — same first-position-wins / dedupe rule as every other
 *  merge here.
 *  Idempotent: a layout with no retired id is returned unchanged (same
 *  reference) so effects keyed on identity don't loop. */
const RENAME_TO: Record<string, string> = {
  budget_hours: 'budget',
  budget_dollars: 'budget',
  burn_rate: 'budget',
  variance: 'budget',
  my_tasks: 'tasks',
  risks: 'tasks',
  ar_aging: 'invoices',
  forma: 'specs',
  profitability: 'budget',
};
const DROPPED_IDS = new Set(['phase_health', 'recent_activity']);

export function migrateReportWidgetIds(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  const hasRetiredId = entries.some(
    (e) => e.id in RENAME_TO || DROPPED_IDS.has(e.id) || e.id === 'team_utilization'
  );
  if (!hasRetiredId) return entries;

  // A rename target that ALREADY exists as a literal entry (e.g. a layout
  // that carries both the legacy `forma` and the current `specs`, or both
  // `profitability` and `budget`) wins outright — pre-seed so every later
  // rename onto that id is dropped, not just renames that collide with
  // each other. Generalizes the pre-existing team_utilization → team rule.
  const seenMerged = new Set<string>();
  for (const target of new Set(Object.values(RENAME_TO))) {
    if (entries.some((e) => e.id === target)) seenMerged.add(target);
  }
  if (entries.some((e) => e.id === 'team')) seenMerged.add('team');

  const out: WidgetLayoutEntry[] = [];
  for (const e of entries) {
    if (DROPPED_IDS.has(e.id)) continue;
    if (e.id === 'team_utilization') {
      if (seenMerged.has('team')) continue;
      seenMerged.add('team');
      out.push({ ...e, id: 'team' });
      continue;
    }
    const target = RENAME_TO[e.id];
    if (target) {
      if (seenMerged.has(target)) continue;
      seenMerged.add(target);
      out.push({ ...e, id: target });
      continue;
    }
    out.push(e);
  }
  return out;
}

/** Retired "Schedule & Milestones" → phases + deliverables (see above).
 *  Returns the input reference when there is nothing to migrate. */
function migrateScheduleTimeline(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  if (!entries.some((e) => e.id === 'schedule_timeline')) return entries;
  const out: WidgetLayoutEntry[] = [];
  const has = (id: string) => entries.some((e) => e.id === id);
  for (const e of entries) {
    if (e.id !== 'schedule_timeline') {
      out.push(e);
      continue;
    }
    if (!has('phases')) out.push({ id: 'phases', size: 'standard', w: 6, hidden: e.hidden });
    if (!has('deliverables')) {
      const wide = e.size === 'expanded' || (e.w ?? 0) >= 9;
      out.push({
        id: 'deliverables',
        size: wide ? 'expanded' : 'standard',
        w: wide ? 12 : 6,
        hidden: e.hidden,
      });
    }
  }
  return out;
}

/** T3c — the Project card (formerly the merged `project` widget: Details +
 *  Design brief + Phase, T3b) moved OUT of the widget system entirely; it
 *  is a static panel Dashboard.tsx renders above the grid, not a layout
 *  entry (see ../pages/projects/ProjectPanel.tsx). A saved layout/starter
 *  that still carries any of the retired ids — the pre-T3b three
 *  (`details`/`design_brief`/`phases`) or the T3b merged `project` — gets
 *  every one of them stripped outright; nothing is inserted in their
 *  place. Idempotent: same reference when there is nothing to migrate.
 *  Runs AFTER `migrateScheduleTimeline` so a very old layout that still
 *  used `schedule_timeline` (which expands to a `phases` entry) gets that
 *  `phases` entry stripped in the same pass. */
const PROJECT_MERGE_IDS = new Set(['details', 'design_brief', 'phases', 'project']);

export function migrateProjectWidgetMerge(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  const hasLegacy = entries.some((e) => PROJECT_MERGE_IDS.has(e.id));
  if (!hasLegacy) return entries;
  return entries.filter((e) => !PROJECT_MERGE_IDS.has(e.id));
}

/**
 * Inject any `required: true` widget def missing from a saved layout —
 * covers users whose saved view/legacy blob predates a widget that later
 * became required (details, phases, deliverables, team, design_brief,
 * specs, forma). Appended entries carry no x/y so WidgetGrid auto-places
 * them below the existing tiles (first-fit, no overlap — see
 * WidgetGrid.tsx's gridItems placement). A required def already present —
 * even `hidden: true` — is left untouched; WidgetGrid forces required
 * widgets visible regardless of a stored hidden flag. Returns the SAME
 * array reference when nothing is missing so effects keyed on identity
 * don't loop.
 */
export function ensureRequiredWidgets<C>(
  layout: WidgetLayoutEntry[],
  defs: WidgetDef<C>[]
): WidgetLayoutEntry[] {
  const missing = defs.filter((d) => d.required && !layout.some((e) => e.id === d.id));
  if (missing.length === 0) return layout;
  return [
    ...layout,
    ...missing.map((d) => ({ id: d.id, size: (d.defaultSize ?? 'standard') as WidgetSize })),
  ];
}

/** The old Project panel's three section ids (projectPanelPrefs.ts) — kept
 *  as a local literal (not imported from `pages/projects/projectPanelPrefs`)
 *  so this pure services module stays independent of the pages layer. */
export type PanelSectionId = 'details' | 'phase' | 'brief';

const DEFAULT_SECTION_ORDER: PanelSectionId[] = ['details', 'phase', 'brief'];

/** Section id → the stock widget id it became (Project Canvas R1, T2). */
const PROJECT_DETAIL_WIDGET_IDS: Record<PanelSectionId, string> = {
  details: 'project_details',
  phase: 'project_phase',
  brief: 'project_brief',
};

/** Sanitizes a (possibly corrupt) saved section order: keeps only known
 *  ids (`details`/`phase`/`brief`), de-dupes preserving first occurrence,
 *  then appends any missing id in default order. `undefined`/`null`/
 *  non-array input returns the default order untouched. Guarantees the
 *  output is always exactly the three known ids, each once. */
function sanitizeSectionOrder(sectionOrder?: PanelSectionId[] | null): PanelSectionId[] {
  if (!Array.isArray(sectionOrder)) return DEFAULT_SECTION_ORDER;
  const known = new Set<PanelSectionId>(DEFAULT_SECTION_ORDER);
  const seen = new Set<PanelSectionId>();
  const out: PanelSectionId[] = [];
  for (const s of sectionOrder) {
    if (known.has(s) && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  for (const s of DEFAULT_SECTION_ORDER) {
    if (!seen.has(s)) out.push(s);
  }
  return out;
}

/** Project Canvas R1 — Details, Phase and Design brief stopped being page
 *  chrome (the old `ProjectPanel.tsx`) and became three stock widgets
 *  (`project_details` / `project_phase` / `project_brief`). A saved
 *  layout, saved view or firm starter from before that change lacks them
 *  entirely (the panel was never a layout entry) — this backfills exactly
 *  the ones missing, inserted at the FRONT in `sectionOrder` (the user's
 *  old panel section order, default Details/Phase/Design brief; sanitized
 *  via `sanitizeSectionOrder` so a corrupt saved order — wrong length,
 *  unknown id, duplicate id — never produces an undefined or duplicate
 *  widget id).
 *
 *  Placement (fix-up, lead design call 2026-10-01 — supersedes the
 *  original "full row each" shape): when ALL THREE are missing, the
 *  sanitized order's first two (o1, o2) go side by side at the top —
 *  `{w:6,x:0,y:0}` / `{w:6,x:6,y:0}` — and the third (o3) spans the full
 *  width directly below — `{w:12,x:0,y:1}`. This is the common case (a
 *  layout that predates the three entirely) and reads as a dashboard on
 *  first open. When only SOME are missing (partial presence — a user
 *  already has one or two from a prior run), each missing one keeps the
 *  prior full-row behavior — `{w:12,x:0,y:0}`, `{w:12,x:0,y:1}`, … — since
 *  there's no "pair" to seat side by side.
 *
 *  Second fix-up (2026-10-01): inserting the three at the top while a
 *  saved entry already sits on row 0 (the common case — nearly every saved
 *  layout has one, typically `attention` at `(0,0,w12)`) used to rely on
 *  WidgetGrid's `verticalCompactor` to push that entry down, and the real
 *  compactor does not do that cleanly (see
 *  `dashboardLayoutMigrations.test.ts`'s real-compactor proof) — it can
 *  cascade o2/o3 past the existing entry instead of seating them above it.
 *  Fixed by shifting the EXISTING entries ourselves instead of leaving it
 *  to the compactor: every existing entry that carries a numeric `y` gets
 *  `y + SHIFT`, where `SHIFT` is one more than the highest row the
 *  inserted widgets occupy (2 when all three are inserted — o1/o2 share
 *  row 0, o3 is row 1; `missingIds.length` when only some are inserted,
 *  since each missing one gets its own row starting at 0). An existing
 *  entry with no numeric `y` (a legacy size-only entry) is left as-is — it
 *  was already following in array order, not occupying a grid row. No
 *  entry is otherwise modified (id, w, x, hidden, settings, size
 *  preserved) and array order (and therefore every other migration's
 *  id-order assertions) is unchanged — only `y` values shift.
 *
 *  An entry already present — even `hidden: true` — is left untouched (a
 *  user who hid one, or already has it from a prior run, never gets a
 *  duplicate). An empty layout means "use the default" and is left empty.
 *  The pre-R1 legacy ids (`details`/`design_brief`/`phases`/`project`,
 *  `migrateProjectWidgetMerge` above) are already stripped by the time
 *  this runs, so they never block insertion. Idempotent: same reference
 *  when nothing is missing. */
export function ensureProjectDetailWidgets(
  entries: WidgetLayoutEntry[],
  sectionOrder?: PanelSectionId[]
): WidgetLayoutEntry[] {
  if (entries.length === 0) return entries;
  const order = sanitizeSectionOrder(sectionOrder);
  const missingIds = order
    .map((s) => PROJECT_DETAIL_WIDGET_IDS[s])
    .filter((id) => !entries.some((e) => e.id === id));
  if (missingIds.length === 0) return entries;

  let inserted: WidgetLayoutEntry[];
  let maxInsertedY: number;
  if (missingIds.length === 3) {
    const [o1, o2, o3] = missingIds;
    inserted = [
      { id: o1, w: 6, x: 0, y: 0 },
      { id: o2, w: 6, x: 6, y: 0 },
      { id: o3, w: 12, x: 0, y: 1 },
    ];
    maxInsertedY = 1;
  } else {
    inserted = missingIds.map((id, i) => ({ id, w: 12, x: 0, y: i }));
    maxInsertedY = missingIds.length - 1;
  }

  const SHIFT = maxInsertedY + 1;
  const shifted = entries.map((e) => (typeof e.y === 'number' ? { ...e, y: e.y + SHIFT } : e));
  return [...inserted, ...shifted];
}

/** Project tokens (SYNC_PLAN §3.5) became a stock widget (`project_tokens`).
 *  A saved layout, saved view or firm starter from before lacks it — this
 *  inserts it ONCE, `{w:6}` beside Details (x:6) when Details is
 *  half-width and that slot is free, else on the row below at x:0 with every
 *  entry below pushed down one. No Details entry (or one with no
 *  numeric `y`) → appended at the end instead. An entry already present —
 *  even `hidden: true` — is left alone; an empty layout means "use the
 *  default" and stays empty. Idempotent; same reference when unchanged. */
export function ensureProjectTokensWidget(entries: WidgetLayoutEntry[]): WidgetLayoutEntry[] {
  if (entries.length === 0 || entries.some((e) => e.id === 'project_tokens')) return entries;
  const details = entries.find((e) => e.id === 'project_details');
  if (!details || typeof details.y !== 'number') {
    const maxY = entries.reduce((m, e) => (typeof e.y === 'number' ? Math.max(m, e.y) : m), -1);
    return [...entries, { id: 'project_tokens', w: 6, x: 0, y: maxY + 1 }];
  }
  // Beside Details (x:6, same row) when Details is half-width and the slot is
  // free; otherwise the row directly below, rest pushed down.
  const slotTaken = entries.some(
    (e) =>
      e.id !== details.id && e.y === details.y && (e.x ?? 0) + (e.w ?? 12) > 6 && (e.x ?? 0) < 12
  );
  if (typeof details.w === 'number' && details.w <= 6 && (details.x ?? 0) === 0 && !slotTaken) {
    const at = entries.findIndex((e) => e.id === 'project_details') + 1;
    return [
      ...entries.slice(0, at),
      { id: 'project_tokens', w: 6, x: 6, y: details.y },
      ...entries.slice(at),
    ];
  }
  const row = details.y + 1;
  const shifted = entries.map((e) =>
    typeof e.y === 'number' && e.y >= row ? { ...e, y: e.y + 1 } : e
  );
  const at = shifted.findIndex((e) => e.id === 'project_details') + 1;
  return [
    ...shifted.slice(0, at),
    { id: 'project_tokens', w: 6, x: 0, y: row },
    ...shifted.slice(at),
  ];
}
